/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { Select } from '@ngxs/store';
import { Observable, Subscription, takeUntil } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { ResourceAdminState } from '../../../../../page-modules/resource/store/admin/resource-admin.state';
import { TEXT_FORM_MAX_WORDS_LIMIT, TEXT_FORM_MIN_WORDS_LIMIT } from '../../../../../shared/constants/constants';
import { LanguageCodeHelper } from '../../../../../shared/helpers/language-code-helper';
import { Organization } from '../../../../../shared/models';
import { MediaUidService } from '../../../../../shared/tiptap/services/media-uid.service';
import { UserAuthState } from '../../../../../user-auth/store/user-auth.state';
import { Form, TextBoxFormContent } from '../../../../models';
import { ApiAiAssistantDataService } from '../../../../services/assistant/ai-assistant-api-data.service';
import { AIAssistantSettings, AI_ASSISTANT_SERVICE } from '../../../../services/assistant/ai-assistant.service';
import { FormAIAssistantSettingsComponent } from './form-ai-assistant-settings/form-ai-assistant-settings.component';
import { FormAiSearchDialogComponent } from './form-ai-search-dialog/form-ai-search-dialog.component';

@Component({
  selector: 'ptl-form-textbox',
  templateUrl: './form-textbox.component.html',
  styleUrls: ['../form.component.scss', './form-textbox.component.scss'],
})
export class FormTextboxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() form: Form;
  @Input() canRemoveItem?: boolean;
  @Input() position?: number | string;

  @Output() formElementAdded = new EventEmitter<Form>();
  @Output() formElementRemoved = new EventEmitter<void>();
  @Output() formElementUpdated = new EventEmitter<Form<TextBoxFormContent>>();

  @ViewChild('formTitleInput') private formTitleInput: ElementRef<HTMLInputElement>;
  @ViewChild('formAiAssistantSettings', { static: false }) private formAiAssistantSettings: FormAIAssistantSettingsComponent;

  @Select(ResourceAdminState.contentStateChanged)
  contentStateChanged$: Observable<boolean>;

  @Select(ResourceAdminState.resourceUid)
  resourceUid$: Observable<string>;

  @Select(UserAuthState.organizationDetails)
  private organizationData$: Observable<Organization>;

  @Select(UserAuthState.aiAssistantFeatureFlag)
  private aiAssistantFeatureFlag$: Observable<boolean>;

  textboxForm: FormGroup;
  expanded = true;
  focused = false;
  hasError = false;
  saveInProgress = false;
  aiAssistantEnabled = false;
  isTemplateSettingsEnabled = false;
  aiAssistantFeatureEnabled = false;
  aiAssistantSettingsUid: string;
  aiAssistantData: AIAssistantSettings;
  isTemplate: boolean;
  saveButtonPressed: boolean;
  updatedAssistantSettings: AIAssistantSettings;
  isDialogOpen = false;
  cardUid: string;
  forceExpand = false;

  private subscriptionEnd$ = new EventEmitter<void>();
  private resourceSubscriptionEnd$ = new EventEmitter<void>();
  private aiFeatureFlagSubscription: Subscription;
  private assistantUpdateSubscription: Subscription;
  private assistantCreateSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    @Inject(AI_ASSISTANT_SERVICE) private aiAssistantService: ApiAiAssistantDataService,
    private mediaUidService: MediaUidService,
  ) {
    this.textboxForm = this.fb.group(
      {
        title: ['', [Validators.required]],
        wordLimit: [TEXT_FORM_MAX_WORDS_LIMIT, [Validators.required, Validators.min(1)]],
        minWordsLimit: [TEXT_FORM_MIN_WORDS_LIMIT, [Validators.required, Validators.min(1)]],
        answerType: ['RICH', [Validators.required]],
        aiAssistantEnabled: [false],
        aiAssistantSettingsUid: [null],
      },
      { validator: this.wordLimitRangeValidator() },
    );
    this.aiFeatureFlagSubscription = this.aiAssistantFeatureFlag$
      .pipe(withLatestFrom(this.organizationData$))
      .subscribe(([flagEnabled]) => {
        this.aiAssistantFeatureEnabled = flagEnabled;
      });
  }

  ngOnChanges(): void {
    if (this.form && !!this.form.uid) {
      this.setFormContent(this.form.content as TextBoxFormContent, this.form.newAddedForm);
    }
  }

  ngOnInit(): void {
    const content = this.form ? (this.form.content as TextBoxFormContent) : null;
    this.isTemplateSettingsEnabled = !!content?.aiAssistantSettingsUid;

    if (this.form && (!!this.form.uid || !!content?.title)) {
      this.setFormContent(content, this.form.newAddedForm);
    }

    this.contentStateChanged$.pipe(takeUntil(this.subscriptionEnd$)).subscribe(() => {
      this.saveInProgress = false;
    });

    this.resourceUid$.pipe(takeUntil(this.resourceSubscriptionEnd$)).subscribe((resourceUid) => {
      if (resourceUid) {
        this.cardUid = resourceUid;
        if (content?.aiAssistantSettingsUid && this.cardUid) {
          this.getAiAssistantData(this.cardUid, content.aiAssistantSettingsUid);
        }
      } else {
        // Fallback to mediaUid from Tiptap editor context
        const mediaUid = this.mediaUidService.getMediaUid();
        if (mediaUid) {
          this.cardUid = mediaUid;
        }
      }
    });

    // Also subscribe to mediaUid changes as fallback
    this.mediaUidService.mediaUid$.pipe(takeUntil(this.resourceSubscriptionEnd$)).subscribe((mediaUid) => {
      if (mediaUid && !this.cardUid) {
        this.cardUid = mediaUid;
      }
    });
    this.forceExpand = this.form?.content?.forceExpand;
  }

  ngAfterViewInit(): void {
    const titleInput = this.formTitleInput?.nativeElement;
    if (this.forceExpand && titleInput) {
      setTimeout(() => {
        const titleLength = titleInput.value.length;
        titleInput.focus();
        titleInput.setSelectionRange(titleLength, titleLength);
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$?.emit();
    this.resourceSubscriptionEnd$?.emit();
    this.aiFeatureFlagSubscription?.unsubscribe();
    this.assistantCreateSubscription?.unsubscribe();
    this.assistantUpdateSubscription?.unsubscribe();
  }

  saveForm(): void {
    this.saveButtonPressed = true;
    this.hasError = false;

    if (!this.cardUid || !this.aiAssistantEnabled) {
      this.proceedFormSave();
      return;
    }

    const aiAssistantModel = this.formAiAssistantSettings?.getValidAssistantSettings() || this.updatedAssistantSettings;

    if (!aiAssistantModel) {
      return;
    }

    if (this.aiAssistantSettingsUid) {
      this.assistantUpdateSubscription = this.aiAssistantService
        .updateAssistantSettings(this.cardUid, this.aiAssistantSettingsUid, aiAssistantModel)
        .subscribe({
          next: ({ isSuccess, value }) => {
            if (isSuccess && value) {
              this.updatedAssistantSettings = value;
            }
            this.proceedFormSave();
          },
          error: () => {
            this.proceedFormSave();
          },
        });
      return;
    }

    this.assistantCreateSubscription = this.aiAssistantService.createAssistantSettings(this.cardUid, aiAssistantModel).subscribe({
      next: ({ isSuccess, value }) => {
        if (value) {
          const uid = value._id;
          this.updatedAssistantSettings = value;
          this.onAssistantSettingsCreated(uid);
          this.aiAssistantSettingsUid = uid;
        }
        this.proceedFormSave();
      },
      error: () => {
        this.proceedFormSave();
      },
    });
  }

  expandForm(): void {
    this.expanded = true;
  }

  collapseForm(event: PointerEvent): void {
    event.stopPropagation();
    this.expanded = false;
    this.forceExpand = false;
  }

  removeForm(): void {
    this.formElementRemoved.emit();

    if (!this.isTemplate && this.aiAssistantSettingsUid) {
      this.aiAssistantService.deleteAssistantSettings(this.cardUid, this.aiAssistantSettingsUid).subscribe();
    }
  }

  openAiSettings(): void {
    this.isTemplateSettingsEnabled = true;
    this.aiAssistantData = null;
  }

  onAiAssistantToggle(event: MatSlideToggleChange): void {
    this.aiAssistantEnabled = event.checked;
    this.textboxForm.patchValue({
      aiAssistantEnabled: this.aiAssistantEnabled,
    });
  }

  onAssistantSettingsCreated(assistantId: string): void {
    this.textboxForm.patchValue({
      aiAssistantSettingsUid: assistantId,
    });
  }

  formChanged(event: null | string): void {
    this.aiAssistantSettingsUid = event;
  }

  saveAsTemplate(event: boolean): void {
    this.isTemplate = event;
  }

  openAiTemplatesDialog(): void {
    this.isDialogOpen = true;
    this.isTemplateSettingsEnabled = false;
    if (this.dialog) {
      this.dialog.closeAll();
    }

    let dialogRef = this.dialog.open(FormAiSearchDialogComponent, {
      width: '90vw',
      maxWidth: '60rem',
      maxHeight: '80vh',
      position: {
        top: '10vh',
        bottom: '10vh',
      },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: ['page-publish-dialog', 'ptl-mat-dialog', 'ptl-mat-dialog-without-padding'],
      backdropClass: 'dialog-backdrop',
      autoFocus: false,
      data: {
        cardUid: this.cardUid,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe((dialogData) => {
        this.updatedAssistantSettings = null;
        if (dialogData?.assistantSettingsUid) {
          this.aiAssistantSettingsUid = dialogData.assistantSettingsUid;
          this.getAiAssistantData(this.cardUid, this.aiAssistantSettingsUid);
        }
        this.isDialogOpen = false;
        this.isTemplateSettingsEnabled = true;
        dialogRef = null;
      });
  }

  wordLimitRangeValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const minWordsLimit = formGroup.get('minWordsLimit')?.value;
      const maxWordsLimit = formGroup.get('wordLimit')?.value;
      if (minWordsLimit < 1 || maxWordsLimit < 1) {
        return { minLimitError: true };
      }
      if (maxWordsLimit < minWordsLimit) {
        return { rangeError: true };
      }
      return null;
    };
  }

  private getAiAssistantData(cardUid: string, aiAssistantSettingsUid: string): void {
    this.aiAssistantService.getAssistantSettingsById(cardUid, aiAssistantSettingsUid).subscribe(({ isSuccess, value }) => {
      if (!value) {
        return;
      }
      this.aiAssistantData = value;
      this.aiAssistantSettingsUid = value?._id;
      this.isTemplate = value?.isTemplate || false;
      this.textboxForm.patchValue(
        {
          aiAssistantSettingsUid: value._id,
        },
        { emitEvent: false },
      );
    });
  }

  private setFormContent(content: TextBoxFormContent, newAddedForm: boolean): void {
    this.expanded = newAddedForm ?? false;
    this.aiAssistantEnabled = content.aiAssistantEnabled ? content.aiAssistantEnabled : false;
    this.aiAssistantSettingsUid = content.aiAssistantSettingsUid ? content.aiAssistantSettingsUid : null;
    this.textboxForm.setValue(
      {
        title: content.title,
        wordLimit: content.wordLimit,
        minWordsLimit: content.minWordsLimit ?? TEXT_FORM_MIN_WORDS_LIMIT,
        aiAssistantEnabled: content.aiAssistantEnabled ?? false,
        aiAssistantSettingsUid: content.aiAssistantSettingsUid ?? null,
        answerType: content.answerType ?? 'RICH',
      },
      { emitEvent: false },
    );
  }

  private proceedFormSave(): void {
    if (this.textboxForm.valid) {
      this.expanded = false;
      this.forceExpand = false;
      this.emitFormUpdate();
      if (!this.form?.uid) {
        this.outputData();
      } else {
        this.formElementAdded.emit(this.form);
      }
      this.saveInProgress = false;
    } else {
      this.hasError = true;
      this.saveInProgress = false;
    }
  }

  private outputData(): void {
    const outputData: Form<TextBoxFormContent> = {
      ...this.form,
      newAddedForm: false,
      content: {
        ...this.textboxForm.value,
        type: 'TEXTBOX',
      } as TextBoxFormContent,
    };

    this.formElementAdded.emit(outputData);
  }

  private emitFormUpdate(): void {
    if (!this.form || !this.textboxForm) {
      return;
    }

    const value = this.textboxForm.getRawValue();
    const existingContent = (this.form.content as TextBoxFormContent) ?? ({} as TextBoxFormContent);
    const content: TextBoxFormContent = {
      ...existingContent,
      ...value,
      type: 'TEXTBOX',
      title: value.title ?? existingContent.title ?? 'Text Box',
      wordLimit: Number(value.wordLimit) || TEXT_FORM_MAX_WORDS_LIMIT,
      minWordsLimit: Number(value.minWordsLimit) || TEXT_FORM_MIN_WORDS_LIMIT,
      answerType: value.answerType ?? existingContent.answerType ?? 'RICH',
      aiAssistantEnabled: !!value.aiAssistantEnabled,
      aiAssistantSettingsUid: value.aiAssistantSettingsUid ?? null,
      forceExpand: this.forceExpand,
      submitted: existingContent?.submitted ?? false,
      formIsActive: existingContent?.formIsActive ?? false,
      isTemplate: this.isTemplate ?? existingContent?.isTemplate ?? false,
    };
    const payload: Form<TextBoxFormContent> = {
      ...(this.form as Form<TextBoxFormContent>),
      newAddedForm: this.form?.uid ? false : (this.form?.newAddedForm ?? false),
      content,
    };

    this.form = payload as Form;
    this.formElementUpdated.emit(payload);
  }
}
